import {
  bestE1rmSet,
  buildSetProposals,
  e1rmPointFromSets,
  rankSwapCandidates,
  type Exercise,
  type LoadableContext,
  type ProposalAnchor,
  type ProposalSource,
  type SetProposal,
  type Workout,
  type WorkoutExerciseEntry,
} from '@heraion/shared';

// Pure history-window math for the Logger, split out of useEntryHistory
// so it carries no React/api coupling and is unit-testable without the
// AsyncStorage/cognito native chain. The Provider owns the cache + fetch;
// this module only transforms an already-fetched window.

// ⭐ LG-59: was 3. His ask on 2026-09-02 was plainly "più sessioni", and the
// widget's second section could not have them — the window handed it three
// sessions no matter how many rows it asked for. Eight is a slice of an
// already-cached list, so it costs no fetch and no query; what caps what the
// athlete sees is the mount's `rowLimit`, not this.
const MAX_RECENT = 8;
// How many similar exercises the third section lists. Keeps it a shortlist
// the athlete can read, not a second catalog.
const ESTIMATE_LIMIT = 5;

export type HistorySession = {
  sessionId: string;
  startedAt: string;
  entry: WorkoutExerciseEntry;
  topE1RM: number;
};

// A similar exercise (ranked by rankSwapCandidates) that DOES have history
// in the cached window — its single most-recent session, offered as a
// starting point beside this exercise's own history.
type HistoryEstimateCandidate = {
  exerciseId: string;
  session: HistorySession;
};

export type HistoryResult = {
  recent: HistorySession[];
  // null when the best session is already in `recent` (no separate row).
  best: HistorySession | null;
  // The nearest exercises the athlete HAS done, with their latest session.
  // ⭐ LG-59: built ALWAYS, not only when this exercise has no history of its
  // own. It used to be the empty-state's consolation prize, which meant that
  // from the athlete's side it simply never appeared — one logged set on the
  // exercise and the section was gone for good. It is now the widget's third
  // section, and «restore it» was Antenore's word for it (2026-09-02).
  // `candidates` may be empty → there is nothing near enough to show.
  estimate: { candidates: HistoryEstimateCandidate[] };
  // EQ-2 fetta 1: the concrete sets to offer, instead of leaving the athlete
  // to adapt the numbers above by hand. Anchored on this exercise's most
  // recent session when there is one, else on the best-ranked similar
  // exercise that has history — and each proposal states which
  // (`source`) and how old the anchor is (`ageDays`).
  // ⛔ Empty is a legitimate answer ("nothing to propose"), never a reason to
  // echo the anchor back unchanged.
  proposals: SetProposal[];
};

export type ProposalContext = {
  /** Injected rather than read from the clock so this module stays pure and
   *  the age assertion is testable. Defaults to now for the live callers. */
  nowMs?: number;
  unit?: 'kg' | 'lb';
  loadable?: LoadableContext;
};

// Turn one session into the proposals it supports. Returns [] when the
// session carries no set the e1RM funnel accepts.
function proposeFrom(
  session: HistorySession,
  source: ProposalSource,
  ctx: ProposalContext,
): SetProposal[] {
  // The argmax lives in ONE place (e1rmPoints.ts). This file wrote its own
  // copy once before — the comment on `e1rmPointFromSets` records it — and
  // very nearly did again for EQ-2, because a proposal needs the raw
  // weight × reps and `E1rmPoint` had nowhere to carry them. `bestE1rmSet`
  // closed that gap instead.
  const top = bestE1rmSet(session.entry.sets);
  if (top === null) return [];
  const anchor: ProposalAnchor = {
    weightKg: top.weightKg,
    reps: top.reps,
    atMs: Date.parse(session.startedAt),
  };
  if (!Number.isFinite(anchor.atMs)) return [];
  return buildSetProposals({
    anchor,
    source,
    nowMs: ctx.nowMs ?? Date.now(),
    ...(ctx.unit ? { unit: ctx.unit } : {}),
    ...(ctx.loadable ? { loadable: ctx.loadable } : {}),
  });
}

// Delegated to the funnel that owns "what counts as an e1RM observation"
// (e1rmPoints.ts, CN-26), so the rep gate, the weight gate and the fail rule
// live in one place. The timestamp is irrelevant here — only the number is read.
function topE1RM(entry: WorkoutExerciseEntry): number {
  return e1rmPointFromSets(0, entry.sets)?.kgPerRep1 ?? 0;
}

// All sessions for one slug in the cached window, newest-first.
export function collectSessions(
  workouts: Workout[],
  exerciseId: string,
): HistorySession[] {
  const sessions: HistorySession[] = [];
  for (const w of workouts) {
    for (const entry of w.entries) {
      if (entry.exerciseId !== exerciseId) continue;
      sessions.push({
        sessionId: w.id,
        startedAt: w.startedAt,
        entry,
        topE1RM: topE1RM(entry),
      });
      // Multiple entries with the same slug in one workout (A-B-A
      // alternation, drop-in extra round) usually share a load profile.
      // First is enough.
      break;
    }
  }
  sessions.sort((a, b) => b.startedAt.localeCompare(a.startedAt));
  return sessions;
}

// When the logged exercise has no history, rank catalog alternatives (same
// scorer the swap picker uses) and surface those that DO have history — the
// lifter picks which one to estimate from. Empty list → honest "can't
// estimate" message. Client-side over the already-cached window; zero DDB.
function buildEstimate(
  workouts: Workout[],
  exerciseId: string,
  catalog: readonly Exercise[] | null,
): { candidates: HistoryEstimateCandidate[] } {
  if (!catalog) return { candidates: [] };
  const current = catalog.find((e) => e.slug === exerciseId);
  if (!current) return { candidates: [] };
  const candidates: HistoryEstimateCandidate[] = [];
  for (const c of rankSwapCandidates(current, catalog)) {
    const sessions = collectSessions(workouts, c.exercise.slug);
    if (sessions.length === 0) continue;
    candidates.push({ exerciseId: c.exercise.slug, session: sessions[0]! });
    if (candidates.length >= ESTIMATE_LIMIT) break;
  }
  return { candidates };
}

export function pickHistory(
  workouts: Workout[],
  exerciseId: string,
  catalog: readonly Exercise[] | null,
  proposalCtx: ProposalContext = {},
): HistoryResult {
  const sessions = collectSessions(workouts, exerciseId);
  const estimate = buildEstimate(workouts, exerciseId, catalog);
  if (sessions.length === 0) {
    // No history of its own → the anchor is the nearest exercise the athlete
    // HAS done. `rankSwapCandidates` already ordered them, so the first one
    // is the best available; the proposal says so rather than pretending the
    // number came from this exercise (§7.3).
    const first = estimate.candidates[0];
    return {
      recent: [],
      best: null,
      estimate,
      proposals: first
        ? proposeFrom(
            first.session,
            {
              kind: 'similar',
              slug: first.exerciseId,
              sessionCount: collectSessions(workouts, first.exerciseId).length,
            },
            proposalCtx,
          )
        : [],
    };
  }
  const ownSource: ProposalSource = {
    kind: 'exercise',
    slug: exerciseId,
    sessionCount: sessions.length,
  };
  // Anchored on the MOST RECENT session, not the best one: "lunedi' 100×10,
  // oggi 100×11" is a step from where the athlete actually is. The best
  // session still shows above as `best` — it is a reference, not a target.
  const proposals = proposeFrom(sessions[0]!, ownSource, proposalCtx);
  const recent = sessions.slice(0, MAX_RECENT);
  let bestIdx = -1;
  let bestVal = 0;
  for (let i = 0; i < sessions.length; i++) {
    if (sessions[i]!.topE1RM > bestVal) {
      bestVal = sessions[i]!.topE1RM;
      bestIdx = i;
    }
  }
  if (bestIdx === -1 || bestVal === 0) {
    return { recent, best: null, estimate, proposals };
  }
  const bestSession = sessions[bestIdx]!;
  const bestIsInRecent = recent.some(
    (r) => r.sessionId === bestSession.sessionId,
  );
  return {
    recent,
    best: bestIsInRecent ? null : bestSession,
    estimate,
    proposals,
  };
}
